import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Trash2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function AdminRange() {
  const nav = useNavigate();
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gcal, setGcal] = useState({ connected: false });
  const [form, setForm] = useState({ starts_at: "", location: "", capacity: 8, instructor_name: "" });
  const [qualForm, setQualForm] = useState({});

  const load = async () => {
    const [a, b, g] = await Promise.all([api.get("/range/slots"), api.get("/admin/range/bookings"), api.get("/oauth/calendar/status")]);
    setSlots(a.data);
    setBookings(b.data);
    setGcal(g.data);
  };
  useEffect(() => { load(); }, []);

  const connectGCal = async () => {
    const { data } = await api.get("/oauth/calendar/login");
    window.location.href = data.authorization_url;
  };
  const disconnectGCal = async () => { await api.delete("/oauth/calendar"); await load(); };

  const create = async (e) => {
    e.preventDefault();
    if (!form.starts_at || !form.location || !form.instructor_name) return;
    const isoOut = new Date(form.starts_at).toISOString();
    await api.post("/admin/range/slots", { ...form, starts_at: isoOut, capacity: Number(form.capacity) });
    setForm({ starts_at: "", location: "", capacity: 8, instructor_name: "" });
    await load();
  };
  const del = async (id) => { await api.delete(`/admin/range/slots/${id}`); await load(); };

  const qualify = async (b) => {
    const v = qualForm[b.booking.user_id] || {};
    if (!v.result) { alert("Choose Pass or Fail"); return; }
    await api.post("/admin/range/qualify", {
      user_id: b.booking.user_id,
      slot_id: b.booking.slot_id,
      result: v.result,
      score: v.score || "",
      notes: v.notes || "",
    });
    await load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <button onClick={() => nav("/admin")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4" /> Admin
      </button>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Administrator</div>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Range Day Management
      </h1>

      <div className="mt-6 flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-sm p-4">
        <span className="text-sm text-slate-700 font-medium">Google Calendar:</span>
        {gcal.connected ? (
          <>
            <span className="text-sm text-green-700 inline-flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Connected — new slots auto-sync
            </span>
            <button data-testid="gcal-disconnect" onClick={disconnectGCal} className="text-xs text-red-700 hover:text-red-900 underline">Disconnect</button>
          </>
        ) : (
          <button data-testid="gcal-connect" onClick={connectGCal} className="text-sm bg-white border border-slate-300 hover:bg-slate-50 px-4 py-1.5 rounded-sm">
            Connect Google Calendar
          </button>
        )}
      </div>

      <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Create Slot</h2>
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-white border border-slate-200 rounded-sm p-5">
        <input data-testid="slot-starts-at" type="datetime-local" required value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2" />
        <input data-testid="slot-location" type="text" required placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2" />
        <input data-testid="slot-instructor" type="text" required placeholder="Instructor name" value={form.instructor_name} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2" />
        <input data-testid="slot-capacity" type="number" required min={1} max={50} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2" />
        <button data-testid="create-slot-btn" type="submit" className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm font-medium text-sm">
          <Plus className="h-4 w-4" /> Add slot
        </button>
      </form>

      <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Posted Slots</h2>
      <div className="space-y-2">
        {slots.length === 0 && <div className="text-sm text-slate-500">No slots yet.</div>}
        {slots.map((s) => (
          <div key={s.id} className="flex items-center justify-between border border-slate-200 rounded-sm px-4 py-3 bg-white">
            <div className="text-sm">
              <span className="font-mono">{new Date(s.starts_at).toLocaleString()}</span> · {s.location} · {s.instructor_name} · {s.booked}/{s.capacity}
            </div>
            <button data-testid={`delete-slot-${s.id}`} onClick={() => del(s.id)} className="text-red-700 hover:text-red-900 text-sm inline-flex items-center gap-1">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Bookings & Qualification</h2>
      <div className="space-y-3">
        {bookings.length === 0 && <div className="text-sm text-slate-500">No bookings yet.</div>}
        {bookings.map((b) => {
          const f = qualForm[b.booking.user_id] || {};
          return (
            <div key={b.booking.user_id} data-testid={`booking-${b.booking.user_id}`} className="border border-slate-200 rounded-sm p-4 bg-white">
              <div className="flex flex-wrap justify-between gap-3 items-start">
                <div>
                  <div className="font-semibold text-slate-900">{b.booking.user_name} <span className="text-xs text-slate-500 font-normal">({b.booking.user_email})</span></div>
                  <div className="text-sm text-slate-700 mt-1">
                    {b.slot ? new Date(b.slot.starts_at).toLocaleString() + " · " + b.slot.location + " · " + b.slot.instructor_name : "Slot removed"}
                  </div>
                  {b.qualified && (
                    <div className="text-xs text-green-700 mt-1 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Qualified ({b.result})
                    </div>
                  )}
                </div>
                {!b.qualified && b.slot && (
                  <div className="flex flex-wrap items-center gap-2">
                    <select data-testid={`qual-result-${b.booking.user_id}`} value={f.result || ""} onChange={(e) => setQualForm({ ...qualForm, [b.booking.user_id]: { ...f, result: e.target.value } })} className="border border-slate-300 rounded-sm px-2 py-1 text-sm">
                      <option value="">Result…</option>
                      <option value="pass">Pass</option>
                      <option value="fail">Fail</option>
                    </select>
                    <input data-testid={`qual-score-${b.booking.user_id}`} type="text" placeholder="Score" value={f.score || ""} onChange={(e) => setQualForm({ ...qualForm, [b.booking.user_id]: { ...f, score: e.target.value } })} className="border border-slate-300 rounded-sm px-2 py-1 text-sm w-24" />
                    <input data-testid={`qual-notes-${b.booking.user_id}`} type="text" placeholder="Notes" value={f.notes || ""} onChange={(e) => setQualForm({ ...qualForm, [b.booking.user_id]: { ...f, notes: e.target.value } })} className="border border-slate-300 rounded-sm px-2 py-1 text-sm w-48" />
                    <button data-testid={`qualify-btn-${b.booking.user_id}`} onClick={() => qualify(b)} className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1 rounded-sm text-sm">Record</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
